import { Circle, CircleCheck, CircleDot, ClipboardList, Eye, Flag } from "lucide-react";
import { useContext, useEffect, useMemo } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

import {
	ResumeBuilderActionsProvider,
	useResumeBuilderActions,
} from "@/app/context/ResumeBuilderActions/ResumeBuilderActionsContext";
import { getRailSection, getRouteByPath } from "@/app/router/resumeRoutes";
import { orderedMiddleSteps, useEducationFirst } from "@/app/router/sectionOrder";
import type { ResumeContextValue } from "@/app/types/resume";

import ResumeContext from "../../../context/ResumeContext";
import Icon from "../../Icon";
import ResumeProgress, { type ResumeSection } from "../ResumeProgress";

/**
 * Shell for the /resume/* builder routes. One responsive tree:
 * - <md: top icon bar, back button, title, fixed bottom progress bar.
 * - md..lg: 72px progress rail; ≥lg: full 260px sidebar (`ml-18 lg:ml-[260px]`).
 * Dialogs are controlled through ResumeBuilderActionsContext — no Bootstrap JS.
 */
const ResumeBuilderLayoutInner = () => {
	const { resumeData } = useContext(ResumeContext) as ResumeContextValue;
	const { openPreview, openFeedback, openSupport } = useResumeBuilderActions();

	const navigate = useNavigate();
	const location = useLocation();
	const currentRoute = location.pathname.split("/")[2];

	const educationFirst = useEducationFirst();

	// Saving a section navigates to the next one, and the browser keeps the scroll
	// position it had — so a long section handed the next one over already scrolled
	// past its own first field. `BrowserRouter` has no ScrollRestoration to turn on.
	useEffect(() => {
		window.scrollTo(0, 0);
	}, [location.pathname]);

	// Guarded registry lookup: entry-funnel routes (e.g. create-resume-options)
	// are not in the registry — fall back to no title and no next-step button.
	const currentEntry = getRouteByPath(currentRoute);
	const currentRouteName = currentEntry?.title ?? null;

	const resumeSections = useMemo<ResumeSection[]>(() => {
		const data: (ResumeSection | null)[] = [
			{
				section: "contact-information",
				title: "Personal Information",
				progressStatus: resumeData.progressStatus.HEADING,
			},
			// Education and Work History swap places with the experience level — see
			// router/sectionOrder.ts, which the builder flow itself is built from.
			...orderedMiddleSteps(educationFirst).map((step) => ({
				section: step.section,
				title: step.title,
				progressStatus: resumeData.progressStatus[step.progressKey],
			})),
			{
				section: "skills",
				title: "Skills",
				progressStatus: resumeData.progressStatus.SKILLS,
			},
			{
				section: "brief-summary",
				title: "Professional Summary",
				progressStatus: resumeData.progressStatus.SUMMARY,
			},
			[...resumeData.extras.personalLinks].length > 0
				? {
						section: "personal-links",
						noClick: true,
						title: "Personal Links",
						progressStatus: "COMPLETED",
						isExtraSection: true,
					}
				: null,
			resumeData.extras.accomplishments
				? {
						noClick: true,
						section: "accomplishments",
						title: "Accomplishments",
						progressStatus: "COMPLETED",
						isExtraSection: true,
					}
				: null,
			resumeData.extras.affiliations
				? {
						noClick: true,
						section: "affiliations",
						title: "Affiliations",
						progressStatus: "COMPLETED",
						isExtraSection: true,
					}
				: null,
			resumeData.extras.certifications
				? {
						section: "certifications",
						noClick: true,
						title: "Certifications",
						progressStatus: "COMPLETED",
						isExtraSection: true,
					}
				: null,
			[...resumeData.extras.languages].length > 0
				? {
						noClick: true,
						section: "languages",
						title: "Languages",
						progressStatus: "COMPLETED",
						isExtraSection: true,
					}
				: null,
			resumeData.extras.additionalInfo
				? {
						section: "additional-information",
						noClick: true,
						title: "Additional Information",
						progressStatus: "COMPLETED",
						isExtraSection: true,
					}
				: null,
			[...resumeData.extras.references].length > 0
				? {
						section: "reference-summary",
						noClick: true,
						title: "References",
						progressStatus: "COMPLETED",
						isExtraSection: true,
					}
				: null,
			resumeData.extras.customExtras.data !== ""
				? {
						section: `custom-extra/${resumeData.extras.customExtras.name}`,
						title: resumeData.extras.customExtras.name,
						progressStatus: "COMPLETED",
						isExtraSection: true,
					}
				: null,
			resumeData.extras.personalProfile.dateOfBirth
				? {
						section: "personal-profile",
						noClick: true,
						title: "Personal Profile",
						progressStatus: "COMPLETED",
						isExtraSection: true,
					}
				: null,
			{
				section: "review",
				title: "Review",
				// Review is the destination, not a section the user fills in, so it
				// never reports COMPLETED. Uses the backend's vocabulary — there is no
				// "INCOMPLETE" in the ResumeProgress enum.
				progressStatus: "NOT STARTED",
			},
		];

		return data.filter((item): item is ResumeSection => item !== null);
	}, [resumeData, educationFirst]);

	return (
		<div className="min-h-screen bg-white">
			<ResumeProgress resumeSections={resumeSections} />

			{/* Mirrors ResumeProgress: a 72px rail on tablets, the full sidebar from lg up. */}
			<section className="min-h-screen bg-white md:ml-18 lg:ml-[260px]">
				<nav className="sticky top-0 z-40 mb-3 flex w-full items-center justify-between border-b bg-white p-3">
					<div className="flex items-center gap-2">
						<Link to="/">
							<Icon iconName="home" />
						</Link>
						{currentRouteName && (
							<p className="mt-0.5 hidden font-bold text-black leading-none md:block">{currentRouteName}</p>
						)}
					</div>
					<div className="flex items-center gap-4">
						<button
							type="button"
							onClick={openPreview}
							className="flex cursor-pointer items-center gap-1.5 font-medium text-primary-700"
						>
							<Eye className="size-5" />
							<span className="hidden md:inline">Preview</span>
						</button>
						<button
							type="button"
							onClick={openFeedback}
							className="flex cursor-pointer items-center gap-1.5 font-medium text-primary-700"
						>
							<ClipboardList className="size-5" />
							<span className="hidden md:inline">Review feedback</span>
						</button>
						<button
							type="button"
							onClick={openSupport}
							className="flex cursor-pointer items-center gap-1.5 font-medium text-primary-700"
						>
							<Flag className="size-5" />
							<span className="hidden md:inline">Report issue</span>
						</button>
					</div>
				</nav>

				{/* Mobile-only back button + section title. */}
				<div className="px-4 md:hidden">
					<button type="button" onClick={() => navigate("/my-resumes")} className="mb-3 cursor-pointer p-0">
						<Icon iconName="back-arrow" size={26} />
					</button>
					{currentRouteName && <p className="font-bold text-black text-lg">{currentRouteName}</p>}
				</div>

				{/* Bottom padding clears the fixed mobile progress strip. `SectionActions`
				    needs none of its own — it is in the flow, so it ends where the section
				    does. */}
				<div className="p-4 pb-14 md:p-6 md:pb-6">
					<div className="max-w-6xl">
						<Outlet />
					</div>
				</div>
			</section>

			{/* Mobile-only progress dots, centred at the very bottom under the section's
			    own buttons. The next-step button that used to sit beside them is gone —
			    `SectionActions` carries the real one, and the two asked the same question
			    twice while squeezing this row off the edge of the screen. */}
			{/* `h-9` is fixed on purpose: `SectionActions` sits directly on top of this
			    strip at a matching `bottom-9`, so a height that grew with its contents
			    would slide under the bar and clip the buttons. */}
			<div className="fixed inset-x-0 bottom-0 z-30 flex h-9 items-center justify-center gap-2 bg-white md:hidden">
				{resumeSections.slice(0, 5).map((item) => {
					// Same resolution the sidebar uses, so both rails agree on the step.
					const isActive = item.section === getRailSection(currentRoute);

					return (
						<button
							key={item.section}
							type="button"
							onClick={() => !item.noClick && navigate(`/resume/${item.section}`)}
							aria-label={item.title}
							aria-current={isActive ? "step" : undefined}
						>
							{item.progressStatus === "COMPLETED" ? (
								<CircleCheck className="size-4 fill-success-default stroke-white" />
							) : isActive ? (
								<CircleDot className="size-4 text-primary-600" />
							) : (
								<Circle className="size-4 text-[#d6e7ff]" />
							)}
						</button>
					);
				})}
			</div>
		</div>
	);
};

const ResumeBuilderLayout = () => (
	<ResumeBuilderActionsProvider>
		<ResumeBuilderLayoutInner />
	</ResumeBuilderActionsProvider>
);

export default ResumeBuilderLayout;
